import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Clock, Calendar, Wallet, ArrowLeft, FileText, CheckCircle, XCircle, AlertCircle, MapPin } from "lucide-react";
import { SimplePaymentUploader } from "@/components/SimplePaymentUploader";
import { useToast } from "@/hooks/use-toast";
import client from "@/Graphql/apolloClient";
import { INVOICE, COMPANY_PAYMENT_OPTIONS } from "@/Graphql/Queries";
import { generateMockData } from "@/Graphql/MockData";
import { CREATE_PAYMENT_METHOD } from "@/Graphql/payment";
import logger from '@/utils/logger';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PriceSummary {
  grossTotal?: number;
  subTotal: number;
  netTotal: number;
  tax: {
    percent: number;
    name: string;
    value: number;
  };
  currency: { code: string; symbol: string; country?: string };
  couponName?: string;
  discountPercent?: number;
  discountedPrice?: number;
}

interface CampaignInventory {
  inventoryAddress: string | undefined;
  inventoryName: string | undefined;
  billboardId: string;
  billboardName: string;
  billboardType: string;
  inventoryPrice: number;
  inventoryThumbnailUrl: string;
  mediaOwnerId: string;
  mediaOwnerName: string;
  inventoryType: string;
  inventoryResolutions: string;
  inventoryReports: {
    totalPotentialViews: number;
    totalUniqueReach: number;
    averageFrequency: number;
    cpm: number;
  };
}

interface InvoiceData {
  id: string;
  name: string;
  dealId: string;
  startDate: { date: string };
  endDate: { date: string };
  summaryReport: {
    potentialViews: string;
    uniqueReach: string;
    averageFrequency: number;
  };
  priceSummary: PriceSummary;
  campaignInventories: CampaignInventory[];
  campaignStatus?: string;
}

interface OfflinePayment {
  id: number;
  campaignId: number;
  userId: number;
  paymentMethod: string;
  totalAmount: number;
  currency: string;
  paymentSlipUrl?: string;
  paymentSlipFilename?: string;
  status: string;
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  invoiceUrl?: string;
  invoiceNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaymentMetadata {
  // Bank Transfer Fields
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  accountType?: string; // Kept for safety, though not in your GQL snippet
  additionalDetails?: {
    swiftCode?: string;
    bankBranchCode?: string;
    bankCountry?: string;
    customFieldLabel?: string;
    customFieldValue?: string;
  };
  // Stripe Fields
  stripeAccessKey?: string;
  // BNPL Fields
  providerName?: string;
  termsUrl?: string;
}

interface CompanyPaymentOption {
  id: string;
  paymentType: string;
  active: boolean;
  paymentMetadata: PaymentMetadata;
}

interface PaymentMethodProps {
  base_path: string;
}

export default function PaymentMethod({ base_path }: PaymentMethodProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [campaignData, setCampaignData] = useState<InvoiceData | null>(null);
  const [billboardCurrencyCode, setBillboardCurrencyCode] = useState("AUD");
  const [loading, setLoading] = useState(true);
  const [paymentOptions, setPaymentOptions] = useState<CompanyPaymentOption[]>([]);

  // New UI features state
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [offlinePaymentType, setOfflinePaymentType] = useState<'bank_transfer' | 'flexible_payment' | null>(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [isPendingApproval, setIsPendingApproval] = useState(false);

  // BNPL (Flexible Payment) form state
  const [bnplCompanyName, setBnplCompanyName] = useState("");
  const [bnplRegistrationNumber, setBnplRegistrationNumber] = useState("");
  const [bnplContactPerson, setBnplContactPerson] = useState("");
  const [bnplPhoneNumber, setBnplPhoneNumber] = useState("");
  const [bnplEmailId, setBnplEmailId] = useState("");
  const [bnplTermsAccepted, setBnplTermsAccepted] = useState(false);
  const [showBnplForm, setShowBnplForm] = useState(false);
  const [bnplSubmitting, setBnplSubmitting] = useState(false);

  // Offline payment upload state
  const [uploadedPayment, setUploadedPayment] = useState<{ uploadURL: string; filename: string } | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const urlCampaignId = searchParams.get('campaign');
  const token = localStorage.getItem("authToken");

  const isMockData = import.meta.env.VITE_APP_MOCK_DATA === "true";
  const campaignId = useMemo(() => {
    return urlCampaignId || (isMockData ? `mock-campaign-${Math.floor(Math.random() * 100000)}` : null);
  }, [urlCampaignId, isMockData]);

  useEffect(() => {
    if (!campaignId) {
      navigate(`${base_path}/my-campaigns`);
      return;
    }

    const fetchInvoiceDetails = async () => {
      setLoading(true);
      try {
        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(INVOICE);
          const data = mockResponse.data;
          setCampaignData(data.paymentInvoice);
          setBillboardCurrencyCode(data.paymentInvoice?.priceSummary?.currency?.code || "AUD");
        } else {
          const { data } = await client.query({
            query: INVOICE,
            variables: {
              accessToken: token,
              campId: campaignId,
            },
            fetchPolicy: "network-only",
          });

          const invoiceData = data.paymentInvoice;
          if (invoiceData) {
            setCampaignData(invoiceData);
            setBillboardCurrencyCode(invoiceData.priceSummary?.currency?.code || "AUD");
          }
        }
      } catch (error) {
        logger.error("Error fetching invoice details:", error);
        toast({
          title: "Error",
          description: "Failed to load campaign details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();

    // Fetch payment options
    const fetchPaymentOptions = async () => {
      try {
        const companyId = import.meta.env.VITE_APP_COMPANY_ID;

        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(COMPANY_PAYMENT_OPTIONS);
          const data = mockResponse.data;
          if (data?.companyPaymentOptions) {
            setPaymentOptions(data.companyPaymentOptions.filter((opt: CompanyPaymentOption) => opt.active && opt.paymentType !== 'STRIPE'));
          }
        } else {
          const { data } = await client.query({
            query: COMPANY_PAYMENT_OPTIONS,
            variables: { companyId },
            fetchPolicy: "network-only",
          });

          if (data?.companyPaymentOptions) {
            setPaymentOptions(data.companyPaymentOptions.filter((opt: CompanyPaymentOption) => opt.active && opt.paymentType !== 'STRIPE'));
          }
        }
      } catch (error) {
        logger.error("Error fetching payment options:", error);
      }
    };

    fetchPaymentOptions();
  }, [campaignId, token, navigate, base_path, toast]);

  const formatCurrency = (amount: number) => {
    return `${billboardCurrencyCode} ${new Intl.NumberFormat('en-AU', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStripePayment = () => {
    if (campaignData?.id) {
      navigate(`${base_path}/checkout?campaign=${campaignData.id}&payment_method=stripe`);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1">
            <CheckCircle className="h-4 w-4 mr-1 inline" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-300 px-3 py-1">
            <XCircle className="h-4 w-4 mr-1 inline" />
            Rejected
          </Badge>
        );
      case 'pending':
      case 'uploaded':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 px-3 py-1">
            <Clock className="h-4 w-4 mr-1 inline" />
            Pending Review
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-300 px-3 py-1">
            {status}
          </Badge>
        );
    }
  };

  const handleBnplSubmit = async () => {
    if (!bnplTermsAccepted) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions to proceed.",
        variant: "destructive",
      });
      return;
    }

    setBnplSubmitting(true);
    try {
      let data;
      
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        // Use mock data
        const mockResponse: any = await generateMockData(CREATE_PAYMENT_METHOD);
        data = mockResponse.data;
      } else {
        // Use real GraphQL API
        const response = await client.mutate({
          mutation: CREATE_PAYMENT_METHOD,
          variables: {
            accessToken: token,
            campaignId: campaignId,
            paymentType: "OFFLINE_BNPL",
            source: "LMX_COMMERCE",
          },
        });
        data = response.data;
      }

      if (data?.createPaymentMethod?.success) {
        toast({
          title: "Application Submitted Successfully",
          description: data.createPaymentMethod.message || "Your BNPL application has been submitted for review. We'll notify you once processed.",
        });
        setShowBnplForm(false);
        setBnplTermsAccepted(false);

        // Optionally navigate to a confirmation page
        setTimeout(() => {
          navigate(`${base_path}/my-campaigns`);
        }, 1500);
      } else {
        throw new Error(data?.createPaymentMethod?.message || "Failed to submit application");
      }
    } catch (error) {
      logger.error("Error submitting BNPL application:", error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Failed to submit BNPL application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBnplSubmitting(false);
    }
  };

  const handleFileUpload = async (files: File[]): Promise<{ uploadURL: string; filename: string }[]> => {
    try {
      // Use FormData for file upload since GraphQL file upload requires special handling
      const formData = new FormData();

      // Create map object for each file
      const mapObject: Record<string, string[]> = {};
      const filesArray: null[] = [];

      files.forEach((file, index) => {
        mapObject[index.toString()] = [`variables.files.${index}`];
        filesArray.push(null);
      });

      // Prepare GraphQL operation
      const operations = JSON.stringify({
        query: `
          mutation UploadOfflinePayment(
            $accessToken: String!
            $files: [Upload!]!
            $campaignId: String!
            $paymentType: String!
            $source: String!
          ) {
            uploadOfflinePayment(
              accessToken: $accessToken
              files: $files
              campaignId: $campaignId
              paymentType: $paymentType
              source: $source
            ) {
              success
              message
              paymentId
              paymentType
              paymentStatus
              status
              paymentProviderMedata {
                bankName
                accountName
                accountNumber
              }
              assets {
                id
                fileName
                filePath
                resolution
                duration
                metadata
              }
              campaign {
                id
                dealId
                name
                bookingSource
                campaignStatus
                startDate {
                  date
                  dateStr
                  dateFmt
                }
                endDate {
                  date
                  dateStr
                  dateFmt
                }
                dsp
                priceSummary {
                  subTotal
                  netTotal
                  pcrEnable
                  currency {
                    country
                    symbol
                    code
                  }
                  tax {
                    name
                    value
                    percent
                  }
                }
                inventoriesSummary {
                  classicInventories
                  digitalInventories
                  packages
                }
                campaignInventories {
                  id
                  dsp
                  startDate
                  endDate
                  companyId
                  inventoryName
                  inventoryId
                  inventoryReferenceId
                  inventoryType
                  inventoryVenueType
                  inventoryThumbnailUrl
                  inventoryPrice
                  inventoryLatitude
                  inventoryLongitude
                  isSpotBased
                  inventoryReports {
                    totalPotentialViews
                    averageFrequency
                    cpm
                    totalReach
                  }
                }
                agency {
                  id
                  name
                }
                company {
                  id
                  name
                }
                user {
                  id
                  name
                }
                summaryReport {
                  potentialViews
                  uniqueReach
                  cpm
                  averageFrequency
                  totalSpots
                }
                reporting {
                  proofOfPlay
                  deliveryReports
                }
                mediaOwners
                buyerDetails {
                  name
                  companyName
                  email
                  mobile
                  address
                }
                createdDate
                lastModifiedDate
              }
            }
          }
        `,
        variables: {
          accessToken: token || "",
          files: filesArray,
          campaignId: campaignId || "",
          paymentType: "OFFLINE_BANK_TRANSFER",
          source: "LMX_COMMERCE",
        },
      });

      const map = JSON.stringify(mapObject);

      formData.append('operations', operations);
      formData.append('map', map);

      // Append each file with its corresponding index
      files.forEach((file, index) => {
        formData.append(index.toString(), file);
      });

      // Use fetch API for file upload
      const response = await fetch(import.meta.env.VITE_APP_GRAPHQL_URI || '', {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      const result = await response.json();

      if (result.data?.uploadOfflinePayment?.success) {
        toast({
          title: "Upload Successful",
          description: result.data.uploadOfflinePayment.message || `${files.length} payment proof file${files.length > 1 ? 's' : ''} uploaded successfully.`,
        });

        // Extract file URLs from assets array
        const assets = result.data.uploadOfflinePayment.assets || [];

        // Return the upload results from the server response
        return assets.map((asset: any) => ({
          uploadURL: asset?.filePath || '',
          filename: asset?.fileName || '',
        }));
      } else if (result.errors) {
        throw new Error(result.errors[0]?.message || "Upload failed");
      } else {
        throw new Error(result.data?.uploadOfflinePayment?.message || "Upload failed");
      }
    } catch (error) {
      logger.error("File upload error:", error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload payment proof. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleUploadComplete = (results: { uploadURL: string; filename: string }[]) => {
    // Store the first upload result for backward compatibility
    if (results.length > 0) {
      setUploadedPayment(results[0]);
      toast({
        title: "Upload Complete",
        description: `${results.length} payment proof file${results.length > 1 ? 's' : ''} uploaded successfully. You can now proceed with your payment.`,
      });
    }
  };

  const handleSendForApproval = async (files: File[]) => {
    try {
      if (!files || files.length === 0) {
        toast({
          title: "No Files",
          description: "Please select at least one file to upload.",
          variant: "destructive",
        });
        return;
      }

      const results = await handleFileUpload(files);

      // Store the first upload result for backward compatibility
      if (results.length > 0) {
        setUploadedPayment(results[0]);
      }

      toast({
        title: "Upload Complete",
        description: `${files.length} payment proof file${files.length > 1 ? 's' : ''} uploaded successfully and sent for approval. Redirecting...`,
      });

      // Redirect to payment proof view page
      setTimeout(() => {
        navigate(`${base_path}/payment-proof-view?campaign=${campaignId}`);
      }, 1000);
    } catch (error) {
      // Re-throw the error so it can be caught by the SimplePaymentUploader component
      throw error;
    }
  };

  if (loading || !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  // Offline payment upload screen
  if (selectedMethod === 'offline') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[95vw] mx-auto px-8 py-8">
          {/* Header with Back Button */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-emerald-600">Offline Payment - Bank Transfer</h1>
              <p className="text-lg text-gray-600">Campaign Name: {campaignData.name}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedMethod(null)}
              className="bg-white/80 backdrop-blur-sm border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Payment Methods
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Payment Upload Section */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-emerald-600">Upload Payment Proof</CardTitle>
                  <CardDescription>
                    Please complete the bank transfer and upload your payment receipt for verification
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold mb-1">Important Notice</p>
                        <p>Please upload a clear photo or scan of your bank transfer receipt. Ensure all details are visible including transaction ID, date, amount, and reference number.</p>
                      </div>
                    </div>
                  </div>

                  <SimplePaymentUploader
                    onUpload={handleFileUpload}
                    onUploadComplete={handleUploadComplete}
                    onSendForApproval={handleSendForApproval}
                    existingPayment={uploadedPayment ? {
                      paymentSlipFilename: uploadedPayment.filename,
                      paymentSlipUrl: uploadedPayment.uploadURL,
                      status: 'uploaded'
                    } : undefined}
                  />
                </CardContent>
              </Card>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
                <h4 className="font-semibold text-emerald-900 mb-4 text-lg">Payment Instructions</h4>

                {/* Bank Transfer Details */}
                <div className="space-y-4 mb-6">
                  {paymentOptions.length > 0 ? (
                    <>
                      <h5 className="font-medium text-emerald-800 mb-3">
                        {(() => {
                          const bankTransferOptions = paymentOptions.filter(opt => opt.paymentType === "OFFLINE_BANK_TRANSFER");
                          return `Choose any of the following bank accounts (${bankTransferOptions.length} ${bankTransferOptions.length === 1 ? 'option' : 'options'} available):`;
                        })()}
                      </h5>

                      {paymentOptions.map((option, index) => {
                        const hasValidData = option.paymentMetadata.bankName?.trim() ||
                          option.paymentMetadata.accountName?.trim() ||
                          option.paymentMetadata.accountNumber?.trim();

                        // Skip this option if all fields are empty
                        if (!hasValidData) return null;

                        return (
                          <div key={option.id} className="bg-white border border-emerald-200 rounded-lg p-4 shadow-sm">
                            <h6 className="font-semibold text-emerald-900 mb-3 text-base">
                              {option.paymentMetadata.bankName?.trim() || `Bank Option ${index + 1}`}
                            </h6>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {option.paymentMetadata.bankName?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Bank Name:</span>
                                  <span className="ml-2 text-gray-900">{option.paymentMetadata.bankName}</span>
                                </div>
                              )}
                              {option.paymentMetadata.additionalDetails?.swiftCode?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Swift Code:</span>
                                  <span className="ml-2 text-gray-900 font-mono">
                                    {option.paymentMetadata.additionalDetails.swiftCode}
                                  </span>
                                </div>
                              )}
                              {option.paymentMetadata.additionalDetails?.bankBranchCode?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Branch Code:</span>
                                  <span className="ml-2 text-gray-900 font-mono">
                                    {option.paymentMetadata.additionalDetails.bankBranchCode}
                                  </span>
                                </div>
                              )}
                              {/* {option.paymentMetadata.accountType?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Account Type:</span>
                                  <span className="ml-2 text-gray-900">{option.paymentMetadata.accountType}</span>
                                </div>
                              )} */}
                              {option.paymentMetadata.accountNumber?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Account Number:</span>
                                  <span className="ml-2 text-gray-900 font-mono">{option.paymentMetadata.accountNumber}</span>
                                </div>
                              )}
                              {option.paymentMetadata.accountName?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Account Name:</span>
                                  <span className="ml-2 text-gray-900">{option.paymentMetadata.accountName}</span>
                                </div>
                              )}
                              {option.paymentMetadata.additionalDetails?.bankCountry?.trim() && (
                                <div>
                                  <span className="font-medium text-gray-700">Country:</span>
                                  <span className="ml-2 text-gray-900">{option.paymentMetadata.additionalDetails.bankCountry}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-semibold mb-1">No payment options available</p>
                          <p>Please contact support for payment instructions.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Reference Details */}
                <div className="bg-white border-2 border-emerald-300 rounded-lg p-4 mb-4">
                  <h6 className="font-semibold text-emerald-900 mb-2">Reference Details (Important)</h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Payment Amount:</span>
                      <span className="text-lg font-bold text-emerald-600">{formatCurrency(campaignData.priceSummary.netTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Campaign Reference:</span>
                      <span className="font-mono text-emerald-800 bg-emerald-100 px-2 py-1 rounded">{campaignData.dealId}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                      <strong>Important:</strong> Always include the Campaign Deal ID "{campaignData.dealId}" in your bank transfer description/reference field.
                    </div>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4">
                  <h6 className="font-semibold text-emerald-900 mb-2">Transfer Instructions</h6>
                  <div className="text-sm text-emerald-800 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">1.</span>
                      <span>Choose any of the {paymentOptions.length > 0 ? paymentOptions.length : ''} bank account{paymentOptions.length !== 1 ? 's' : ''} above for your transfer</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">2.</span>
                      <span>Transfer the exact amount: <strong>{formatCurrency(campaignData.priceSummary.netTotal)}</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">3.</span>
                      <span>Include Campaign Deal ID <strong>{campaignData.dealId}</strong> in transfer description</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">4.</span>
                      <span>Upload clear photo/scan of payment receipt for verification</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">5.</span>
                      <span>Payment will be reviewed and approved within 24-48 hours</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold">6.</span>
                      <span>Campaign goes live immediately after payment approval</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign Summary Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-emerald-600">Payment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Campaign Name</div>
                    <div className="text-gray-900">{campaignData.name}</div>
                    <div className="text-sm text-gray-500">{campaignData.dealId}</div>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span>{formatDate(campaignData.startDate.date)} - {formatDate(campaignData.endDate.date)}</span>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    {/* Gross Total */}
                    <div className="flex justify-between text-sm">
                      <span>Gross Total:</span>
                      <span>{formatCurrency(campaignData.priceSummary.grossTotal || 0)}</span>
                    </div>

                    {/* Discount Applied - Always show */}
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>
                        Discount Applied
                        {campaignData.priceSummary.discountedPrice && campaignData.priceSummary.discountedPrice > 0 && campaignData.priceSummary.grossTotal && campaignData.priceSummary.grossTotal > 0
                          ? ` (${Math.round((campaignData.priceSummary.discountedPrice / campaignData.priceSummary.grossTotal) * 100)}%)`
                          : campaignData.priceSummary.discountPercent && campaignData.priceSummary.discountPercent > 0
                            ? ` (${Math.round(campaignData.priceSummary.discountPercent)}%)`
                            : ' (0%)'}:
                      </span>
                      <span>- {formatCurrency(campaignData.priceSummary.discountedPrice || 0)}</span>
                    </div>

                    {/* Subtotal */}
                    <div className="flex justify-between text-sm">
                      <span>Subtotal{campaignData.priceSummary.currency.country?.toLowerCase() === "singapore" ? ` (Excl. ${campaignData.priceSummary.tax.name})` : ""}:</span>
                      <span>{formatCurrency(campaignData.priceSummary.subTotal)}</span>
                    </div>

                    {/* Tax - Only show for Singapore */}
                    {campaignData.priceSummary.currency.country?.toLowerCase() === "singapore" && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span>{campaignData.priceSummary.tax.name} ({Math.round(campaignData.priceSummary.tax.percent)}%):</span>
                          <span>{formatCurrency(campaignData.priceSummary.tax.value)}</span>
                        </div>
                        <Separator />
                      </>
                    )}

                    {/* Grand Total */}
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Grand Total:</span>
                      <span className="text-emerald-600">{formatCurrency(campaignData.priceSummary.netTotal)}</span>
                    </div>
                  </div>

                  {campaignData?.campaignInventories && campaignData?.campaignInventories.length > 0 && (
                    <TooltipProvider>
                      <div className="pt-4 border-t space-y-6">
                        {/* Digital Inventories Section */}
                        {(() => {
                          const digitalInventories = campaignData.campaignInventories.filter(
                            (inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital')
                          );
                          return digitalInventories.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">Digital Inventories</h4>
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 rounded-full px-2 py-0 h-5 text-xs font-medium border-none">
                                  {digitalInventories.length} {digitalInventories.length === 1 ? 'item' : 'items'}
                                </Badge>
                              </div>
                              {digitalInventories.map((inventory: CampaignInventory, index: number) => (
                                <div key={`digital-${index}`} className="flex items-start gap-3">
                                  <img
                                    src={inventory?.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                    alt={inventory?.inventoryName}
                                    className="w-12 h-12 rounded-md object-cover border border-gray-100"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{inventory?.inventoryName}</p>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-start gap-1 text-xs text-gray-500 mt-0.5 cursor-help">
                                          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                          <span className="truncate">{inventory?.inventoryAddress}</span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="max-w-[250px] text-xs bg-gray-900 text-white">
                                        <p>{inventory?.inventoryAddress}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Classic Inventories Section */}
                        {(() => {
                          const classicInventories = campaignData.campaignInventories.filter(
                            (inv: CampaignInventory) => {
                              const type = inv.inventoryType?.toLowerCase() ?? '';
                              return type.includes('classic') || type.includes('static');
                            }
                          );
                          return classicInventories.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">Classic Inventories</h4>
                                <Badge variant="secondary" className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full px-2 py-0 h-5 text-xs font-medium border-none">
                                  {classicInventories.length} {classicInventories.length === 1 ? 'item' : 'items'}
                                </Badge>
                              </div>
                              {classicInventories.map((inventory: CampaignInventory, index: number) => (
                                <div key={`classic-${index}`} className="flex items-start gap-3">
                                  <img
                                    src={inventory?.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                    alt={inventory?.inventoryName}
                                    className="w-12 h-12 rounded-md object-cover border border-gray-100"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{inventory?.inventoryName}</p>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-start gap-1 text-xs text-gray-500 mt-0.5 cursor-help">
                                          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                          <span className="truncate">{inventory?.inventoryAddress}</span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="max-w-[250px] text-xs bg-gray-900 text-white">
                                        <p>{inventory?.inventoryAddress}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Package Inventories Section */}
                        {(() => {
                          const packages = campaignData.campaignInventories.filter(
                            (inv: CampaignInventory) => inv.inventoryType?.toLowerCase() === 'package'
                          );
                          return packages.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">Package Inventories</h4>
                                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-full px-2 py-0 h-5 text-xs font-medium border-none">
                                  {packages.length} {packages.length === 1 ? 'item' : 'items'}
                                </Badge>
                              </div>
                              {packages.map((inventory: CampaignInventory, index: number) => (
                                <div key={`package-${index}`} className="flex items-start gap-3">
                                  <img
                                    src={inventory?.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                    alt={inventory?.inventoryName}
                                    className="w-12 h-12 rounded-md object-cover border border-gray-100"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{inventory?.inventoryName}</p>
                                    <div className="flex items-start gap-1 text-xs text-gray-500 mt-0.5">
                                      <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                      <span className="truncate">Multiple Locations</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </TooltipProvider>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[95vw] mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`${base_path}/checkout?campaign=${campaignData.id}`)}
            className="bg-white/80 backdrop-blur-sm border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Checkout
          </Button>
        </div>

        {/* Header */}
        <div className="relative overflow-hidden bg-white rounded-2xl p-8 mb-8 border border-emerald-200 shadow-sm">
          <div className="text-center">
            <div className="mb-4">
              <h1 className="text-3xl font-bold text-emerald-900">
                Choose Payment Method
              </h1>
            </div>
            <div className="flex items-center justify-center gap-8">
              <p className="text-lg text-gray-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                Campaign Name: {campaignData.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Methods */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Stripe */}
              <Card className="group cursor-pointer bg-white border-2 border-emerald-200 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg flex flex-col">
                <CardHeader className="text-center flex-1 flex flex-col">
                  <div className="bg-emerald-50 rounded-2xl p-6 mx-auto w-fit mb-4">
                    <div className="w-20 h-12 flex items-center justify-center">
                      <div className="text-2xl font-bold text-blue-600">Stripe</div>
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-emerald-900">
                    Stripe
                  </CardTitle>
                  <CardDescription className="text-gray-600 flex-1 flex items-center justify-center">
                    Secure card payments • Instant processing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                    onClick={handleStripePayment}
                  >
                    Pay with Card
                  </Button>
                </CardContent>
              </Card>

              {/* Offline Payment - Active */}
              <Card className="group cursor-pointer bg-white border-2 border-emerald-200 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg flex flex-col">
                <CardHeader className="text-center flex-1 flex flex-col">
                  <div className="bg-emerald-50 rounded-2xl p-4 mx-auto w-fit mb-4">
                    <Upload className="h-8 w-8 text-emerald-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-emerald-900">
                    Offline Payment
                  </CardTitle>
                  <CardDescription className="text-gray-600 flex-1 flex items-center justify-center">
                    Upload payment slip • Manual verification
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                    onClick={() => setSelectedMethod('offline')}
                  >
                    Upload Slip
                  </Button>
                </CardContent>
              </Card>

              {/* FlexiPay - Active (Singapore Only) */}
              {(campaignData?.priceSummary?.currency?.country === "Singapore" || isMockData) && (
                <Card className="group cursor-pointer bg-white border-2 border-emerald-200 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg flex flex-col">
                  <CardHeader className="text-center flex-1 flex flex-col">
                    <div className="bg-emerald-50 rounded-2xl p-4 mx-auto w-fit mb-4">
                      <Wallet className="h-8 w-8 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-emerald-900">
                      FlexiPay
                    </CardTitle>
                    <CardDescription className="text-gray-600 flex-1 flex items-center justify-center">
                      Buy Now, Pay Later • Credit assessment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                      onClick={() => setShowBnplForm(true)}
                    >
                      Apply Now
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Payoneer - Disabled */}
              <Card className="cursor-not-allowed bg-white border-2 border-gray-200 opacity-60 flex flex-col">
                <CardHeader className="text-center flex-1 flex flex-col">
                  <div className="bg-gray-50 rounded-2xl p-6 mx-auto w-fit mb-4">
                    <div className="w-20 h-12 flex items-center justify-center">
                      <div className="text-2xl font-bold text-orange-600">Payoneer</div>
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-700">
                    Payoneer
                  </CardTitle>
                  <CardDescription className="text-gray-500 flex-1 flex items-center justify-center">
                    International payments • Global reach
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-gray-300 text-gray-500 font-semibold py-3 rounded-lg cursor-not-allowed"
                    disabled
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>

              {/* PayPal - Disabled */}
              <Card className="cursor-not-allowed bg-white border-2 border-gray-200 opacity-60 flex flex-col">
                <CardHeader className="text-center flex-1 flex flex-col">
                  <div className="bg-gray-50 rounded-2xl p-6 mx-auto w-fit mb-4">
                    <div className="w-20 h-12 flex items-center justify-center">
                      <div className="text-2xl font-bold text-blue-700">PayPal</div>
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-700">
                    PayPal
                  </CardTitle>
                  <CardDescription className="text-gray-500 flex-1 flex items-center justify-center">
                    PayPal account • Secure payments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-gray-300 text-gray-500 font-semibold py-3 rounded-lg cursor-not-allowed"
                    disabled
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Security Info Section */}
            <div className="mt-8 p-6 bg-emerald-50 rounded-2xl border border-emerald-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
                <h3 className="text-lg font-semibold text-emerald-900">
                  Secure Payment Processing
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  256-bit SSL encryption
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  PCI DSS compliant
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  Real-time verification
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Summary */}
          <div>
            <Card className="bg-white border-2 border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-xl font-bold text-emerald-900">
                  <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-2">
                  <div className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Campaign Name
                  </div>
                  <div className="pl-4">
                    <div className="text-emerald-900 font-semibold">{campaignData.name}</div>
                    <div className="text-sm text-gray-600 font-mono">{campaignData.dealId}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <span className="text-gray-700">{formatDate(campaignData.startDate.date)} - {formatDate(campaignData.endDate.date)}</span>
                </div>

                <Separator />

                <div className="space-y-3">
                  {/* Gross Total */}
                  <div className="flex justify-between text-sm p-2 rounded-lg bg-gray-50">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                      Gross Total:
                    </span>
                    <span className="font-mono">{formatCurrency(campaignData.priceSummary.grossTotal || 0)}</span>
                  </div>

                  {/* Discount Applied - Always show */}
                  <div className="flex justify-between text-sm p-2 rounded-lg bg-emerald-50">
                    <span className="flex items-center gap-2 text-emerald-700">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      Discount Applied
                      {campaignData.priceSummary.discountedPrice && campaignData.priceSummary.discountedPrice > 0 && campaignData.priceSummary.grossTotal && campaignData.priceSummary.grossTotal > 0
                        ? ` (${Math.round((campaignData.priceSummary.discountedPrice / campaignData.priceSummary.grossTotal) * 100)}%)`
                        : campaignData.priceSummary.discountPercent && campaignData.priceSummary.discountPercent > 0
                          ? ` (${Math.round(campaignData.priceSummary.discountPercent)}%)`
                          : ' (0%)'}:
                    </span>
                    <span className="font-mono text-emerald-700">- {formatCurrency(campaignData.priceSummary.discountedPrice || 0)}</span>
                  </div>

                  {/* Subtotal */}
                  <div className="flex justify-between text-sm p-2 rounded-lg bg-gray-50">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                      Subtotal{campaignData.priceSummary.currency.country?.toLowerCase() === "singapore" ? ` (Excl. ${campaignData.priceSummary.tax.name})` : ""}:
                    </span>
                    <span className="font-mono">{formatCurrency(campaignData.priceSummary.subTotal)}</span>
                  </div>

                  {/* Tax - Only show for Singapore */}
                  {campaignData.priceSummary.currency.country?.toLowerCase() === "singapore" && (
                    <>
                      <div className="flex justify-between text-sm p-2 rounded-lg bg-gray-50">
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          {campaignData.priceSummary.tax.name} ({Math.round(campaignData.priceSummary.tax.percent)}%):
                        </span>
                        <span className="font-mono">{formatCurrency(campaignData.priceSummary.tax.value)}</span>
                      </div>

                      <Separator />
                    </>
                  )}

                  {/* Grand Total */}
                  <div className="flex justify-between font-bold text-lg p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <span className="flex items-center gap-2 text-emerald-900">
                      <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                      Grand Total:
                    </span>
                    <span className="text-emerald-700 font-mono text-xl">{formatCurrency(campaignData.priceSummary.netTotal)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* BNPL / Flexible Payment Application Form Dialog */}
      <Dialog open={showBnplForm} onOpenChange={setShowBnplForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Wallet className="h-5 w-5" />
              Flexible Payment Application (BNPL)
            </DialogTitle>
            <DialogDescription className="text-left mx-4">
              Complete the form below to request financing for your campaign. Your information will be shared with our financing partner for credit assessment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Campaign Invoice Section */}
            {/* <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Campaign Invoice
              </h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Invoice will be generated upon submission</p>
                  <p className="text-xs text-gray-500 mt-1">PDF format • Download available after approval</p>
                </div>
                <Button variant="outline" size="sm" disabled className="text-emerald-700 border-emerald-300">
                  <FileText className="h-4 w-4 mr-2" />
                  View Invoice
                </Button>
              </div>
            </div> */}

            {/* Campaign Details Section */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Campaign Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Campaign ID:</span>
                  <p className="font-medium text-gray-900">{campaignData?.dealId}</p>
                </div>
                <div>
                  <span className="text-gray-500">Invoice ID:</span>
                  <p className="font-medium text-gray-900">INV-{campaignData?.id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Campaign Name:</span>
                  <p className="font-medium text-gray-900">{campaignData?.name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Duration:</span>
                  <p className="font-medium text-gray-900">
                    {Math.ceil((new Date(campaignData.endDate.date).getTime() - new Date(campaignData.startDate.date).getTime()) / (1000 * 60 * 60 * 24)+1)} days
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Start Date:</span>
                  <p className="font-medium text-gray-900">{formatDate(campaignData.startDate.date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">End Date:</span>
                  <p className="font-medium text-gray-900">{formatDate(campaignData.endDate.date)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Total Budget:</span>
                  <p className="font-bold text-emerald-600 text-lg">{formatCurrency(campaignData.priceSummary.netTotal)}</p>
                </div>
              </div>

              {/* Inventory Details */}
              {campaignData?.campaignInventories?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-emerald-50 rounded-lg border border-emerald-200 overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-emerald-600 text-white px-4 py-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">

                        Inventory Details
                      </h4>
                    </div>
                    
                    {/* Table Content */}
                    <div className="bg-white">
                      {/* Digital Category */}
                      {campaignData.campaignInventories.some((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital')) && (
                        <>
                          <div className="bg-blue-50 px-4 py-2 border-l-4 border-blue-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-blue-900">
                                Digital ({campaignData.campaignInventories.filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital')).length} Digital{campaignData.campaignInventories.filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital')).length > 1 ? 's' : ''})
                              </span>
                              <span className="font-bold text-blue-900">
                                {formatCurrency(campaignData.campaignInventories
                                  .filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital'))
                                  .reduce((sum, inv) => sum + parseFloat(inv.inventoryPrice?.toString() || '0'), 0))}
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('digital'))
                            .map((inv: CampaignInventory, idx: number) => (
                            <div key={`digital-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                              <img
                                src={inv.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                alt={inv.inventoryName}
                                className="w-12 h-12 rounded object-cover border border-gray-200"
                              />
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-gray-900 mb-1">{inv.inventoryName}</h5>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-medium text-emerald-600">
                                  {formatCurrency(parseFloat(inv.inventoryPrice?.toString() || '0'))}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Classic Category */}
                      {campaignData.campaignInventories.some((inv: CampaignInventory) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package')))) && (
                        <>
                          <div className="bg-green-50 px-4 py-2 border-l-4 border-green-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-green-900">
                                Classic ({campaignData.campaignInventories.filter((inv: CampaignInventory) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package')))).length} Classic)
                              </span>
                              <span className="font-bold text-green-900">
                                {formatCurrency(campaignData.campaignInventories
                                  .filter((inv: CampaignInventory) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package'))))
                                  .reduce((sum, inv) => sum + parseFloat(inv.inventoryPrice?.toString() || '0'), 0))}
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: CampaignInventory) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package'))))
                            .map((inv: CampaignInventory, idx: number) => (
                            <div key={`classic-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                              <img
                                src={inv.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                alt={inv.inventoryName}
                                className="w-12 h-12 rounded object-cover border border-gray-200"
                              />
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-gray-900 mb-1">{inv.inventoryName}</h5>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-medium text-emerald-600">
                                  {formatCurrency(parseFloat(inv.inventoryPrice?.toString() || '0'))}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Package Category */}
                      {campaignData.campaignInventories.some((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('package')) && (
                        <>
                          <div className="bg-purple-50 px-4 py-2 border-l-4 border-purple-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-purple-900">
                                Package ({campaignData.campaignInventories.filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('package')).length} Package)
                              </span>
                              <span className="font-bold text-purple-900">
                                {formatCurrency(campaignData.campaignInventories
                                  .filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('package'))
                                  .reduce((sum, inv) => sum + parseFloat(inv.inventoryPrice?.toString() || '0'), 0))}
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: CampaignInventory) => inv.inventoryType?.toLowerCase().includes('package'))
                            .map((inv: CampaignInventory, idx: number) => (
                            <div key={`package-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                              <img
                                src={inv.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop'}
                                alt={inv.inventoryName}
                                className="w-12 h-12 rounded object-cover border border-gray-200"
                              />
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-gray-900 mb-1">{inv.inventoryName}</h5>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-medium text-emerald-600">
                                  {formatCurrency(parseFloat(inv.inventoryPrice?.toString() || '0'))}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Buyer Profile Details
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-emerald-600" />
                  Buyer Profile Details
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    placeholder="Enter company name"
                    value={bnplCompanyName}
                    onChange={(e) => setBnplCompanyName(e.target.value)}
                    className={bnplCompanyName ? "bg-emerald-50 border-emerald-200" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registrationNumber">Registration Number *</Label>
                  <Input
                    id="registrationNumber"
                    placeholder="e.g., 202012345A"
                    value={bnplRegistrationNumber}
                    onChange={(e) => setBnplRegistrationNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Contact Person Name *</Label>
                  <Input
                    id="contactPerson"
                    placeholder="Full name"
                    value={bnplContactPerson}
                    onChange={(e) => setBnplContactPerson(e.target.value)}
                    className={bnplContactPerson ? "bg-emerald-50 border-emerald-200" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <Input
                    id="phoneNumber"
                    placeholder="+61 XXXX XXXX"
                    value={bnplPhoneNumber}
                    onChange={(e) => setBnplPhoneNumber(e.target.value)}
                    className={bnplPhoneNumber ? "bg-emerald-50 border-emerald-200" : ""}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="emailId">Email ID *</Label>
                  <Input
                    id="emailId"
                    type="email"
                    placeholder="company@example.com"
                    value={bnplEmailId}
                    onChange={(e) => setBnplEmailId(e.target.value)}
                    className={bnplEmailId ? "bg-emerald-50 border-emerald-200" : ""}
                  />
                </div>
              </div>
            </div> */}

            {/* BNPL Terms and Conditions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                BNPL Terms & Conditions
              </h4>
              <div className="text-sm text-gray-700 mb-4">
                <p className="mb-2">By proceeding with Flexible Payment, you agree to:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Credit assessment by our financing partner</li>
                  <li>Repayment terms as per the financing agreement</li>
                  <li>Late payment fees may apply</li>
                </ul>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white rounded border border-amber-300">
                <Checkbox
                  id="termsAcceptance"
                  checked={bnplTermsAccepted}
                  onCheckedChange={(checked) => setBnplTermsAccepted(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="termsAcceptance" className="text-sm leading-relaxed cursor-pointer">
                  <span className="font-medium text-gray-900">I agree to share my campaign and company details with the financing partner for credit assessment.</span>
                  <span className="text-red-500 ml-1">*</span>
                </Label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBnplForm(false);
                  setBnplTermsAccepted(false);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!bnplTermsAccepted || bnplSubmitting}
                onClick={handleBnplSubmit}
              >
                {bnplSubmitting ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
